import { useEffect, useRef, useState } from "react";
import { Camera, ImageUp, Loader2, X } from "lucide-react";
import { C, labelStyle } from "../../theme/tokens";
import { SectionHeader, PrimaryButton, TimeframeSelect } from "../../components/common/primitives";
import { scanFrameStyle, ScanCorners } from "./ScanFrame";

// The selected chart timeframe is passed to the scanner prompt as context.
const TIMEFRAMES = ["S5", "S15", "M1", "M5", "M10", "M15", "M30"];

/* POST /bot/scanner/ {photo, timeframe} -> a binary signal (same response
  shape as POST /bot/signal/). The active in-flight scan itself is
   owned by App() via useActiveScan() and rendered full-screen as
   ActiveSignalOverlay, exactly like the Signals bot — see
   hooks/useActiveScan.js for why. This screen only ever has to pick
   a file, a timeframe, and kick off the upload. */
export function ScannerScreen({ t, wide, activeScan }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [timeframe, setTimeframe] = useState("M1");

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the exact same file later
    if (!picked) return;
    if (!picked.type?.startsWith("image/")) {
      setFileError(t.scan_file_hint_error);
      return;
    }
    setFileError(null);
    setFile(picked);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(picked);
    });
  };

  const clearFile = () => {
    setFile(null);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  };

  const handleAnalyze = () => {
    if (!file) return;
    activeScan.uploadScan(file, timeframe);
  };

  // Once the upload is accepted, the full-screen overlay (rendered in
  // App()) takes over completely until the result is recorded — reset
  // the local picker so this screen is a blank slate for the next scan
  // by the time the overlay closes and the user lands back here.
  useEffect(() => {
    if (!activeScan.entry) return;
    setFile(null);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScan.entry]);

  return (
    <div>
      <SectionHeader eyebrow="Photo → signal" title={t.scan_title} sub={t.scan_sub} wide={wide} />
      <div
        style={{
          padding: wide ? "4px 0 20px" : "4px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          maxWidth: wide ? 520 : undefined,
          margin: wide ? "0 auto" : undefined,
          width: "100%",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <div>
          <div style={labelStyle}>{t.scan_timeframe}</div>
          <TimeframeSelect value={timeframe} onChange={setTimeframe} options={TIMEFRAMES} accent={C.scanBright} />
        </div>

        {!previewUrl && (
          <button
            onClick={openFilePicker}
            style={{
              ...scanFrameStyle({ dashed: true }),
              padding: "40px 20px",
              textAlign: "center",
              background: C.panel1,
              cursor: "pointer",
              width: "100%",
            }}
          >
            <ScanCorners />
            <div
              style={{
                width: 52,
                height: 52,
                margin: "0 auto 14px",
                borderRadius: 14,
                background: `linear-gradient(135deg, ${C.scanDeep} 0%, ${C.scan} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Camera size={24} color={C.scanBright} />
            </div>
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: 14.5,
                color: C.primary,
              }}
            >
              {t.scan_upload}
            </div>
            <div style={{ fontSize: 12, color: C.mutedB, marginTop: 5 }}>{t.scan_hint}</div>
          </button>
        )}

        {previewUrl && (
          <div
            style={{
              ...scanFrameStyle(),
              background: C.panel1,
            }}
          >
            <ScanCorners />
            <img
              src={previewUrl}
              alt=""
              style={{ display: "block", width: "100%", maxHeight: 360, objectFit: "contain" }}
            />
            <button
              onClick={clearFile}
              disabled={activeScan.submitting}
              aria-label={t.scan_change_photo}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 30,
                height: 30,
                borderRadius: 9,
                border: `1px solid ${C.hairline}`,
                background: "rgba(10,12,10,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: activeScan.submitting ? "not-allowed" : "pointer",
              }}
            >
              <X size={15} color={C.primary} />
            </button>
            <button
              onClick={openFilePicker}
              disabled={activeScan.submitting}
              style={{
                position: "absolute",
                bottom: 10,
                right: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: 10,
                border: `1px solid ${C.hairline}`,
                background: "rgba(10,12,10,0.65)",
                color: C.primary,
                fontSize: 12,
                fontWeight: 600,
                cursor: activeScan.submitting ? "not-allowed" : "pointer",
              }}
            >
              <ImageUp size={13} /> {t.scan_change_photo}
            </button>
          </div>
        )}

        {fileError && (
          <div style={{ color: "#C4604A", fontSize: 12.5, textAlign: "center" }}>{fileError}</div>
        )}
        {activeScan.error && (
          <div style={{ color: "#C4604A", fontSize: 12.5, textAlign: "center" }}>
            {activeScan.error === "trial_limit" ? t.scan_error_trial_limit : t.scan_error}
          </div>
        )}

        <PrimaryButton
          onClick={handleAnalyze}
          disabled={!file || activeScan.submitting}
          fromColor={C.scanBright}
          toColor={C.scan}
          shadowColor={C.scanBright}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%" }}>
            {activeScan.submitting ? (
              <>
                <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} />
                {t.scan_analyzing}
              </>
            ) : (
              t.scan_cta
            )}
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
}
